import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableRow, CircularProgress, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import SettingsErrorAlert from './SettingsErrorAlert';

const SystemInformation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/settings/system-information');
      setData(response.data.data);
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load system information',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  if (error) return (
    <Box>
      <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />
      <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { setError(null); load(); }}>Retry</Button>
    </Box>
  );

  const renderPortBlock = (ports) => (
    <Table size="small">
      <TableBody>
        {Object.entries(ports || {}).map(([key, value]) => (
          <TableRow key={key}>
            <TableCell sx={{ textTransform: 'capitalize' }}>{key.replaceAll('_', ' ')}</TableCell>
            <TableCell>{value ?? '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>System Information</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Base Tenant Information</Typography>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell>Frontend Version</TableCell><TableCell>{data?.base_tenant_information?.frontend?.version || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Backend Version</TableCell><TableCell>{data?.base_tenant_information?.backend?.version || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database Version</TableCell><TableCell>{data?.base_tenant_information?.database?.version || '-'}</TableCell></TableRow>
              </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Frontend Ports</Typography>
            {renderPortBlock(data?.base_tenant_information?.frontend?.ports)}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Backend Ports</Typography>
            {renderPortBlock(data?.base_tenant_information?.backend?.ports)}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Database Ports</Typography>
            {renderPortBlock(data?.base_tenant_information?.database?.details)}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Super Admin App Information</Typography>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell>Frontend Version</TableCell><TableCell>{data?.super_admin_app_information?.frontend?.version || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Backend Version</TableCell><TableCell>{data?.super_admin_app_information?.backend?.version || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database Name</TableCell><TableCell>{data?.super_admin_app_information?.database?.details?.database || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database User</TableCell><TableCell>{data?.super_admin_app_information?.database?.details?.user || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database Host</TableCell><TableCell>{data?.super_admin_app_information?.database?.details?.host || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database Port</TableCell><TableCell>{data?.super_admin_app_information?.database?.details?.port || '-'}</TableCell></TableRow>
              </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Frontend Ports</Typography>
            {renderPortBlock(data?.super_admin_app_information?.frontend?.ports)}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Backend Ports</Typography>
            {renderPortBlock(data?.super_admin_app_information?.backend?.ports)}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Database Ports</Typography>
            {renderPortBlock(data?.super_admin_app_information?.database?.ports)}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Deployment Details</Typography>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell>Docker Desktop Container Mode</TableCell><TableCell>{data?.deployment_details?.docker_desktop_container_mode?.path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>K8s Local Base Tenant</TableCell><TableCell>{data?.deployment_details?.local_kubernetes?.base_tenant?.path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>K8s Local Super Admin</TableCell><TableCell>{data?.deployment_details?.local_kubernetes?.super_admin?.path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>AWS Kubernetes</TableCell><TableCell>{data?.deployment_details?.cloud_kubernetes?.aws?.path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Azure Kubernetes</TableCell><TableCell>{data?.deployment_details?.cloud_kubernetes?.azure?.path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>GCP Kubernetes</TableCell><TableCell>{data?.deployment_details?.cloud_kubernetes?.gcp?.path || '-'}</TableCell></TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Component Versions (Standard: MAJOR.YYYY.WW.MINOR)</Typography>
            <Table size="small">
              <TableBody>
                {Object.entries(data?.versions || {}).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{key.replaceAll('_', ' ')}</TableCell>
                    <TableCell>{value?.version || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemInformation;

